<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition"
            @click:outside="$emit('close', false)">
    <v-card>
      <v-app-bar dark color="primary" fixed absolute elevation="4" class="fixed-toolbar">
        <v-btn icon dark @click="$emit('close', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t(`tax.title.${edit ? 'update' : 'create'}`, { name: form.name }) }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn dark text :loading="loading" :disabled="!submittable" @click="submitForm">
          {{ $t(`tax.button.${edit ? 'update' : 'add'}`) }}
        </v-btn>
      </v-app-bar>

      <v-container fluid class="toolbar-padding">
        <v-row justify="center">
          <v-col cols="12" md="8" lg="6">
            <v-card class="elevation-0 mb-6">
              <v-card-text>
                <v-form ref="taxForm" v-model="isFormValid" lazy-validation>
                  <v-row>
                    <v-col cols="12" md="8">
                      <v-text-field
                        ref="inputName"
                        v-model="form.name"
                        :label="$t('tax.form.label.name')"
                        :rules="nameRules"
                        required
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        ref="rateField"
                        v-model="form.rate"
                        :label="$t('tax.form.label.rate')"
                        :rules="rateRules"
                        type="number"
                        suffix="%"
                        min="0"
                        max="100"
                        required
                        outlined
                        dense
                        @focus="handleRateFieldFocus"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        v-model="form.type"
                        :items="types"
                        :label="$t('tax.form.label.type')"
                        item-text="label"
                        item-value="value"
                        :rules="typeRules"
                        required
                        outlined
                        dense
                      ></v-select>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        v-model="form.option"
                        :items="options"
                        :label="$t('tax.form.label.option')"
                        item-text="label"
                        item-value="value"
                        outlined
                        dense
                        clearable
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor" bottom right>
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
export default {
  name: 'createTax',
  props: {
    display: {
      type: Boolean,
      required: true
    },
    tax: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      dialog: this.display,
      form: {
        name: '',
        rate: '',
        type: '',
        option: null
      },
      types: [
        {
          label: this.$t('tax.form.type.additive'),
          value: 'additive'
        },
        {
          label: this.$t('tax.form.type.inclusive'),
          value: 'inclusive'
        }
      ],
      options: [
        {
          label: this.$t('tax.form.option.new_items'),
          value: 'new'
        },
        {
          label: this.$t('tax.form.option.existing_items'),
          value: 'existing'
        },
        {
          label: this.$t('tax.form.option.both'),
          value: 'new_existing'
        }
      ],
      loading: false,
      isFormValid: true,

      // Validation rules
      nameRules: [
        v => !!v || 'Tax name is required',
        v => v.length >= 3 || 'Tax name must be at least 3 characters'
      ],
      rateRules: [
        v => !!v || 'Tax rate is required',
        v => (v >= 0 && v <= 100) || 'Tax rate must be between 0 and 100'
      ],
      typeRules: [
        v => !!v || 'Tax type is required'
      ],

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle'
    }
  },
  computed: {
    submittable () {
      return this.isFormValid && this.form.name && this.form.rate && this.form.type
    },
    edit () {
      return this.tax && this.tax.id
    }
  },
  watch: {
    display (val) {
      this.dialog = val
    },
    dialog (val) {
      if (!val) {
        this.$emit('close', false)
      } else {
        this.initializeForm()
      }
    }
  },
  methods: {
    initializeForm () {
      if (this.edit) {
        this.form.name = this.tax.name || ''
        this.form.rate = this.tax.rate || ''
        this.form.type = this.tax.type || ''
        this.form.option = this.tax.option || null
      } else {
        this.resetForm()
      }

      // Focus on name input after initialization
      this.$nextTick(() => {
        if (this.$refs.inputName) {
          this.$refs.inputName.focus()
        }
      })
    },

    resetForm () {
      this.form = {
        name: '',
        rate: '',
        type: '',
        option: null
      }

      if (this.$refs.taxForm) {
        this.$refs.taxForm.resetValidation()
      }
    },

    showSnackbar (text, type = 'success') {
      this.snackbarText = text
      this.snackbarColor = type
      this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'
      this.snackbar = true
    },

    handleRateFieldFocus () {
      if (!this.form.rate) {
        // For Vuetify text fields, we need to use the input element
        this.$nextTick(() => {
          const inputEl = this.$refs.rateField.$el.querySelector('input')
          if (inputEl) {
            inputEl.select()
          }
        })
      }
    },

    async submitForm () {
      if (this.$refs.taxForm.validate()) {
        this.askQuestion(this.$t(`tax.confirm.${this.edit ? 'update' : 'create'}`), async () => {
          this.loading = true
          try {
            let data
            if (this.tax.id) {
              data = await this.$taxService.updateTax(this.tax.id, this.form)
              this.showSnackbar(this.$t('tax.message.updated'))
              this.$emit('updated', data)
            } else {
              data = await this.$taxService.createTax(this.form)
              this.showSnackbar(this.$t('tax.message.created'))
              this.$emit('created', data)
            }
            this.$emit('tax-ok')
            this.resetForm()
            this.$emit('close', false)
          } catch (error) {
            // Error is already handled by the global interceptor
            console.log('Component caught error in submitForm:', error)
          } finally {
            this.loading = false
          }
        })
      }
    }
  },
  mounted () {
    this.initializeForm()
  }
}
</script>

<style>
/* Add padding to account for fixed toolbar */
.toolbar-padding {
  padding-top: 64px; /* Height of the toolbar */
}

/* Fixed toolbar styles */
.fixed-toolbar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  width: 100% !important;
}

/* Ensure proper spacing on mobile */
@media (max-width: 599px) {
  .toolbar-padding {
    padding-top: 56px; /* Smaller toolbar height on mobile */
  }
}
</style>
